import { useEffect, useState } from 'react'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/customer-ui/tabs'
import { Button } from '@/components/ui/button'
import { LogOut, User, Zap, FileText } from 'lucide-react'
import zeresLogo from './assets/zeres-logo-full.png'
import GeneralInfoTab from './components/GeneralInfoTab'
import { isGeneralInfoComplete } from './components/general-info-storage'
import ChargingStationsTab from './components/ChargingStationsTab'
import QuarterlyReportsTab from './components/QuarterlyReportsTab'
import Footer from './components/Footer'
import ChangePasswordDialog from './components/ChangePasswordDialog'
import { useCustomerAccountAuthentication } from '@modules/manifera-user/resources/js/pages/customer-app/use-customer-account-authentication.tsx'
import './styles.css'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ApiChargingStation } from '../../../types'
import client from '@/http/customer-client'
import z from 'zod'
import { reportSchema } from '../../page-report-overview/schema'

export default function EvJourneyDashboard() {
  const { t } = useTranslation()
  const { customer, performLogout, isLoggingOut, fetchAccount } =
    useCustomerAccountAuthentication()
  const [activeTab, setActiveTab] = useState(() =>
    isGeneralInfoComplete() ? 'stations' : 'general'
  )
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: ['customer-stations'],
      queryFn: async () => {
        const res = await client.get<{ data: ApiChargingStation[] }>(
          '/app-api/me/stations'
        )

        return res.data.data
      },
      staleTime: 60 * 1000
    })

    void queryClient.prefetchQuery({
      queryKey: ['customer-reports'],
      queryFn: async () => {
        const response = await client.get('/app-api/me/reports')

        const parsed = z
          .object({
            data: z.array(reportSchema)
          })
          .parse(response.data)

        return parsed.data
      },
      staleTime: 60 * 1000
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='ev-journey-scope bg-background min-h-screen'>
      <header className='bg-card'>
        <div className='mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6'>
          <img src={zeresLogo} alt='Zeres' className='h-10 w-auto' />
          <div className='flex items-center gap-3'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='bg-background hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground h-9 rounded-md border-2 px-4 text-black'
              style={{ borderColor: 'var(--primary)' }}
              onClick={() => {
                setIsChangePasswordOpen(true)
              }}
            >
              Verander wachtwoord
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                void performLogout()
              }}
              className='text-muted-foreground gap-2'
              disabled={isLoggingOut}
            >
              <LogOut className='h-4 w-4' />
              <span className='hidden sm:inline'>{t('Logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className='container mx-auto min-h-[calc(100vh-300px)] max-w-4xl px-4 py-6 sm:px-6 sm:py-10'>
        {/* <div className='mb-6 overflow-hidden rounded-lg'>
          <img
            src={dashboardBanner}
            alt='Zeres banner'
            className='h-auto w-full'
          />
        </div> */}

        <div className='mb-8'>
          <div className='flex flex-wrap items-baseline gap-2'>
            <h1 className='text-foreground text-2xl font-bold sm:text-3xl'>
              {t('My account')}
            </h1>
          </div>
          <p className='text-muted-foreground mt-1 text-base'>
            {customer?.email ?? 'account'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className='mb-8 grid h-auto w-full grid-cols-3'>
            <TabsTrigger
              value='general'
              className='gap-2 py-3 text-xs sm:text-sm'
            >
              <User className='h-4 w-4' />
              <span className='hidden sm:inline'>{t('My details')}</span>
            </TabsTrigger>
            <TabsTrigger
              value='stations'
              className='gap-2 py-3 text-xs sm:text-sm'
            >
              <Zap className='h-4 w-4' />
              <span className='hidden sm:inline'>
                {t('My charging stations')}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value='reports'
              className='gap-2 py-3 text-xs sm:text-sm'
            >
              <FileText className='h-4 w-4' />
              <span className='hidden sm:inline'>{t('Reports')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value='general' className='animate-fade-in'>
            <GeneralInfoTab
              customer={customer ?? undefined}
              onSaveSuccess={() => {
                setActiveTab('stations')
              }}
              fetchAccount={fetchAccount}
            />
          </TabsContent>

          <TabsContent value='stations' className='animate-fade-in'>
            <ChargingStationsTab customer={customer ?? undefined} />
          </TabsContent>

          <TabsContent value='reports' className='animate-fade-in'>
            <QuarterlyReportsTab />
          </TabsContent>
        </Tabs>
      </main>

      <ChangePasswordDialog
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
      />

      <Footer />
    </div>
  )
}
